import React, { useEffect, useMemo, useState } from "react";

const stages = [
  { label: "Inventory", detail: "Confirming available stock", completeAt: 25 },
  { label: "Routing", detail: "Building warehouse routes", completeAt: 40 },
  { label: "Carriers", detail: "Collecting live quotes", completeAt: 90 },
  { label: "Selection", detail: "Comparing total cost", completeAt: 100 },
];

export default function FreightLoading({ progress = 0, status = "pending" }) {
  const [elapsed, setElapsed] = useState(0);
  const safeProgress = Math.min(Math.max(Number(progress) || 0, 0), 100);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(interval);
  }, []);

  const activeIndex = useMemo(() => {
    const index = stages.findIndex((stage) => safeProgress < stage.completeAt);
    return index === -1 ? stages.length - 1 : index;
  }, [safeProgress]);

  return (
    <div>
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--primary-color)] opacity-40 motion-safe:animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--primary-color)]" />
              </span>
              <span className="text-xs font-medium uppercase tracking-wide text-[var(--primary-color)]">
                {status === "pending" ? "Queued" : "Rating in progress"}
              </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              Building the freight plan
            </h3>
            <p className="mt-1 max-w-xl text-sm leading-6 text-gray-600">
              Matching inventory to warehouse routes and comparing live carrier
              quotes.
            </p>
          </div>

          <div className="flex items-baseline gap-3 sm:text-right">
            <span className="text-3xl font-semibold text-gray-900">
              {safeProgress}%
            </span>
            <span className="text-xs text-gray-500">{elapsed}s elapsed</span>
          </div>
        </div>

        <div
          className="h-2 overflow-hidden rounded-full bg-gray-200"
          role="progressbar"
          aria-label="Freight rating progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={safeProgress}
        >
          <div
            className="h-full rounded-full bg-[var(--primary-color)] transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: `${safeProgress}%` }}
          />
        </div>

        <ol className="mt-6 grid gap-3 sm:grid-cols-4">
          {stages.map((stage, index) => {
            const isComplete = safeProgress >= stage.completeAt;
            const isActive = index === activeIndex && !isComplete;
            return (
              <li
                key={stage.label}
                className={`rounded-md border px-4 py-3 ${
                  isActive
                    ? "border-[var(--primary-color)] bg-white shadow-sm"
                    : isComplete
                      ? "border-green-200 bg-green-50"
                      : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                      isComplete
                        ? "bg-green-600 text-white"
                        : isActive
                          ? "bg-[var(--primary-color)] text-white"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {isComplete ? "✓" : index + 1}
                  </span>
                  {isActive && (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--primary-color)]">
                      Working
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {stage.label}
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  {stage.detail}
                </p>
              </li>
            );
          })}
        </ol>

        <p className="mt-5 border-t border-gray-200 pt-4 text-xs text-gray-500">
          Keep this page open. Rates appear as soon as carrier comparison is
          complete.
        </p>
      </div>
    </div>
  );
}
